import React, { useState, useEffect } from 'react';
import { Package, Filter, Download, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { reportsService, InventoryReportFilters, InventoryReportItem } from '../../services/reportsService';
import hotelService from '../../services/hotelService';
import roomService from '../../services/roomService';
import Pagination from '../../components/ui/pagination';

const InventoryReports: React.FC = () => {
  // Get current date and 30 days in the future
  const getDefaultDates = () => {
    const today = new Date();
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + 30);
    
    return {
      date_from: today.toISOString().split('T')[0],
      date_to: futureDate.toISOString().split('T')[0]
    };
  };

  const [filters, setFilters] = useState<InventoryReportFilters>({
    page: 1,
    limit: 50,
    ...getDefaultDates()
  });
  const [inventory, setInventory] = useState<InventoryReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hotels, setHotels] = useState<any[]>([]);
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    total_pages: 0
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    fetchHotels();
    fetchInventory();
  }, []);

  useEffect(() => {
    if (filters.hotel_id) {
      fetchRoomTypes(filters.hotel_id);
    } else {
      setRoomTypes([]);
    }
  }, [filters.hotel_id]);

  const fetchHotels = async () => {
    try {
      const response = await hotelService.getHotels(1, 1000);
      setHotels(response.data);
    } catch (error) {
      console.error('Error fetching hotels:', error);
    }
  };

  const fetchRoomTypes = async (hotelId: number) => {
    try {
      const response = await roomService.getRoomTypes(hotelId, 1, 1000);
      setRoomTypes(response.data);
    } catch (error) {
      console.error('Error fetching room types:', error);
    }
  };

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const response = await reportsService.getInventoryReport(filters);
      setInventory(response.data.inventory);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching inventory report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handleSearch = () => {
    fetchInventory();
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
    fetchInventory();
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));
    fetchInventory();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const getGroupedInventory = () => {
    // Group by hotel first, then by date
    const grouped = inventory.reduce((acc, item) => {
      const hotelKey = item.hotel_id;
      
      if (!acc[hotelKey]) {
        acc[hotelKey] = {
          hotel_id: item.hotel_id,
          hotel_name: item.hotel_name,
          city: item.city,
          country: item.country,
          dates: {}
        };
      }
      
      const dateKey = item.inventory_date;
      if (!acc[hotelKey].dates[dateKey]) {
        acc[hotelKey].dates[dateKey] = {
          inventory_date: item.inventory_date,
          roomTypes: []
        };
      }
      
      acc[hotelKey].dates[dateKey].roomTypes.push(item);
      return acc;
    }, {} as Record<number, any>);
    
    return Object.values(grouped);
  };

  const getOccupancyColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600 bg-red-100';
    if (percentage >= 70) return 'text-yellow-600 bg-yellow-100';
    if (percentage >= 50) return 'text-blue-600 bg-blue-100';
    return 'text-green-600 bg-green-100';
  };

  const getAvailabilityColor = (available: number, total: number) => {
    const percentage = (available / total) * 100;
    if (percentage <= 10) return 'text-red-600 bg-red-100';
    if (percentage <= 30) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <Package className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Inventory Reports</h1>
        </div>
        <p className="text-gray-600">
          Track room availability and occupancy across all hotels
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          </div>
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
          >
            <span>{showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters</span>
            {showAdvancedFilters ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>
        
        {/* Basic Filters */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Basic Filters</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hotel
              </label>
              <select
                value={filters.hotel_id || ''}
                onChange={(e) => handleFilterChange('hotel_id', e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Hotels</option>
                {hotels.map((hotel) => (
                  <option key={hotel.id} value={hotel.id}>
                    {hotel.hotel_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date From
              </label>
              <input
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date To
              </label>
              <input
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Advanced Filters</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Room Type
                </label>
                <select
                  value={filters.room_type_id || ''}
                  onChange={(e) => handleFilterChange('room_type_id', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Room Types</option>
                  {roomTypes.map((roomType) => (
                    <option key={roomType.id} value={roomType.id}>
                      {roomType.room_type_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={handleSearch}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search className="w-4 h-4" />
            <span>{loading ? 'Searching...' : 'Search'}</span>
          </button>

          <button
            onClick={() => {
              setFilters({ page: 1, limit: 50, ...getDefaultDates() });
              fetchInventory();
            }}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Reset to Default
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Inventory Report Results
            </h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                Total: {pagination.total} records
              </span>
              <button className="flex items-center space-x-2 px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200">
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading inventory data...</p>
          </div>
        ) : inventory.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No inventory data found matching your criteria</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-r border-gray-300">
                      Hotel
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 border-r border-gray-300">
                      Date
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 border-r border-gray-300">
                      Total Rooms
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                      Available Rooms
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {getGroupedInventory().map((hotel: any, hotelIndex: number) => 
                    Object.values(hotel.dates).map((dateGroup: any, dateIndex: number) => {
                      const totalRooms = dateGroup.roomTypes.reduce((sum: number, room: any) => sum + room.total_rooms, 0);
                      const totalAvailable = dateGroup.roomTypes.reduce((sum: number, room: any) => sum + room.rooms_available, 0);
                      
                      return (
                        <tr key={`${hotel.hotel_id}-${dateGroup.inventory_date}`} className="border-b border-gray-300">
                          <td className="px-4 py-3 border-r border-gray-300">
                            <div className="font-medium text-gray-900">
                              {hotel.hotel_name}
                            </div>
                            <div className="text-sm text-gray-600">
                              {hotel.city}, {hotel.country}
                            </div>
                            
                            {/* Room Types as Columns */}
                            <div className="mt-3 overflow-x-auto">
                              <table className="w-full border border-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                                      Room Type
                                    </th>
                                    <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                                      Total
                                    </th>
                                    <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                                      Booked
                                    </th>
                                    <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                                      Available
                                    </th>
                                    <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      %
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dateGroup.roomTypes.map((roomType: any, roomIndex: number) => (
                                    <tr key={roomIndex} className={roomIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="px-2 py-1 text-center border-r border-gray-200">
                                        <div className="text-xs font-medium text-gray-900">
                                          {roomType.room_type_name}
                                        </div>
                                      </td>
                                      <td className="px-2 py-1 text-center border-r border-gray-200">
                                        <div className="text-xs text-gray-600">
                                          {roomType.total_rooms}
                                        </div>
                                      </td>
                                      <td className="px-2 py-1 text-center border-r border-gray-200">
                                        <div className="text-xs text-gray-600">
                                          {roomType.rooms_booked}
                                        </div>
                                      </td>
                                      <td className="px-2 py-1 text-center border-r border-gray-200">
                                        <div className={`text-xs font-medium ${getAvailabilityColor(roomType.rooms_available, roomType.total_rooms)}`}>
                                          {roomType.rooms_available}
                                        </div>
                                      </td>
                                      <td className="px-2 py-1 text-center">
                                        <div className={`text-xs font-medium ${getOccupancyColor(roomType.occupancy_percentage)}`}>
                                          {roomType.occupancy_percentage}%
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center border-r border-gray-300">
                            <div className="font-medium text-blue-600">
                              {formatDate(dateGroup.inventory_date)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center border-r border-gray-300">
                            <div className="font-medium text-gray-900">
                              {totalRooms}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="font-medium text-gray-900">
                              {totalAvailable}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Enhanced Pagination */}
            {pagination.total > 0 && (
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.total_pages}
                totalItems={pagination.total}
                itemsPerPage={pagination.limit}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                showItemsPerPage={true}
                itemsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InventoryReports;
